import { useState, useMemo, useEffect } from 'react';
import { IconPlus } from '@tabler/icons-react';

import { sortDataWithDirection } from '../core/utils';
import AlertMessage from '../components/alerts/AlertMessage';
import { sortDirections } from '../core/constants';
import Loader from '../components/loader/Loader';
import { useTags } from '../store/tags/hook';
import TagsTable from '../components/tags/TagsTable';
import TagFormModal from '../components/tags/TagFormModal';
import Button from '../components/buttons/Button';
import Sorters from '../components/tables/Sorters';

const sortByOptions = [
  { label: 'Name', value: 'name' },
  { label: 'Date', value: 'created_at' },
];

const Tags = () => {
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState(sortDirections.desc);
  const [createOpen, setCreateOpen] = useState(false);
  const { loading, error, success, tags, getTags, setSuccess, setError, createTag } = useTags();

  useEffect(() => {
    getTags();
  }, [getTags]);

  // description is required by lists-api — it is the audience context handed to
  // content generation, so TagFormModal collects both fields.
  const onSave = async ({ name, description }) => {
    try {
      await createTag({ name, description }).unwrap();
      setCreateOpen(false);
    } catch (err) {
      // Error handled by redux
    }
  };

  const customTagCount = useMemo(() => tags.filter((tag) => !tag.is_reserved).length, [tags]);
  const reservedTagCount = tags.length - customTagCount;

  const data = useMemo(() => {
    let filteredData = tags;
    if (sortBy) {
      filteredData = sortDataWithDirection(filteredData, sortBy, sortDirection);
    }
    // Reserved tags first regardless of sort: they are the ones the product
    // guarantees exist, and they anchor the donor-bucket workflow.
    return [...filteredData].sort((a, b) => Number(!!b.is_reserved) - Number(!!a.is_reserved));
  }, [tags, sortBy, sortDirection]);

  return (
    <div className="w-full space-y-6">
      <div className="flex justify-between space-x-3">
        <div className="mb-auto">
          <h1 className="text-h3 dark:text-white-100">Tags</h1>
          {!!tags.length && (
            <p className="mt-1 text-sm text-gray-600">
              {reservedTagCount} reserved · {customTagCount} custom
            </p>
          )}
        </div>

        <div className="flex items-center space-x-3">
          <Sorters
            options={sortByOptions}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
          />

          <Button title="Create Tag" color="primary" LeftIcon={IconPlus} onClick={() => setCreateOpen(true)} />
        </div>
      </div>

      <div className="relative">
        <TagsTable data={data} />

        {loading && <Loader />}
      </div>

      <TagFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={onSave}
        loading={loading}
        error={typeof error === 'string' ? error : ''}
      />

      <AlertMessage
        open={!!success}
        message={typeof success === 'string' ? success : 'Operation completed successfully!'}
        onClose={() => setSuccess(false)}
        severity="success"
      />

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => setError(false)}
        severity="error"
      />
    </div>
  );
};

export default Tags;
